import { useEffect } from 'react';
import { useApolloClient, gql } from '@apollo/client';
import { useWebSocketReconnect } from './useWebSocketReconnect';

/**
 * Subscription for bulk procedure batch updates (e.g., CSV upload).
 *
 * When a CSV is uploaded, the backend publishes a ProceduresBatchUpdated event
 * with procedure IDs grouped by date. This subscription listens for those events
 * and triggers a refetch of the schedule query for affected dates.
 */

const PROCEDURES_BATCH_UPDATED_SUBSCRIPTION = gql`
  subscription ProceduresBatchUpdated($hospitalId: Long!) {
    proceduresBatchUpdated(hospitalId: $hospitalId) {
      hospitalId
      dates
      procedureIds
      source
    }
  }
`;

interface ProceduresBatchSubscriptionOptions {
  hospitalId: number;
  date: string;
  enabled?: boolean;
  onRefresh?: (date: string) => void;
}

export function useProceduresBatchSubscription({
  hospitalId,
  date,
  enabled = true,
  onRefresh,
}: ProceduresBatchSubscriptionOptions) {
  const client = useApolloClient();
  const reconnectCount = useWebSocketReconnect();

  useEffect(() => {
    if (!enabled || !hospitalId) {
      return;
    }

    if (reconnectCount > 0) {
      console.log(`[BatchUpdate] WebSocket reconnected (count: ${reconnectCount}), re-subscribing`);
    }

    const subscription = client
      .subscribe({
        query: PROCEDURES_BATCH_UPDATED_SUBSCRIPTION,
        variables: { hospitalId },
      })
      .subscribe({
        next: ({ data }) => {
          if (!data?.proceduresBatchUpdated) {
            return;
          }

          const { dates, procedureIds, source } = data.proceduresBatchUpdated;

          console.log(
            `[BatchUpdate] 🔄 Batch update received: ${procedureIds.length} procedures across ${dates.length} dates (source: ${source})`
          );

          // Only refetch if the batch includes the current date
          if (dates.includes(date)) {
            console.log(`[BatchUpdate] ✅ Refetching schedule for ${date}`);

            client.refetchQueries({
              include: ['scheduleProcedures', 'dailyScheduleEntities'],
            });

            if (onRefresh) {
              onRefresh(date);
            }
          } else {
            console.log(
              `[BatchUpdate] ⏭️  Skipping refresh - current date ${date} not in batch dates: ${dates.join(', ')}`
            );
          }
        },
        error: err => {
          console.error('[BatchUpdate] Subscription error:', err);
        },
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [client, hospitalId, date, enabled, reconnectCount, onRefresh]);
}
